import { EditCarrierDto, EditCarrierOrganisationDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { CARRIER_REVIEW_APPROVED } from '../../../carriers/logic/carrier-review';
import { CarrierGroupForm } from './form';

const OUTPUT_KEYS = [
  'name',
  'image',
  'review',
  'kioskRegistrationPolicy',
  'qrRegistrationPolicy',
  'registrationTarget',
  'loginMethod'
] as const;

type OutputKey = (typeof OUTPUT_KEYS)[number];

/** What a save may carry. Display-only fields are dropped so they never count as a change. */
export type CarrierGroupFormOutput = Pick<CarrierGroupForm, OutputKey>;

/** The modified subset of the output. `null` is how the form says a value was cleared. */
export type CarrierGroupChanges = { [K in OutputKey]?: string | null };

export function toCarrierGroupFormOutput(form: CarrierGroupForm): CarrierGroupFormOutput {
  const output: CarrierGroupFormOutput = {};
  for (const key of OUTPUT_KEYS) {
    if (form[key] !== undefined) {
      output[key] = form[key];
    }
  }
  return output;
}

/** Approval has its own endpoint. Nothing unapproves a carrier. */
export function isApproval(changes: CarrierGroupChanges): boolean {
  return changes.review === CARRIER_REVIEW_APPROVED;
}

/**
 * One call for a unit. Cleared values are dropped: the endpoint cannot unset a name or image, and
 * a login method is only changed here, never cleared.
 */
export function toEditCarrierDto(changes: CarrierGroupChanges): EditCarrierDto {
  const dto: EditCarrierDto = {};
  if (changes.name) dto.name = changes.name;
  if (changes.image) dto.image = changes.image;
  if (changes.kioskRegistrationPolicy) dto.kioskRegistrationPolicy = changes.kioskRegistrationPolicy;
  if (changes.qrRegistrationPolicy) dto.qrRegistrationPolicy = changes.qrRegistrationPolicy;
  if (changes.loginMethod) dto.loginMethod = changes.loginMethod;
  return dto;
}

export interface OrganisationChanges {
  organisation?: EditCarrierOrganisationDto;
  registrationTarget?: string;
  /** Applied to the registration target: the new one if it changed in the same save. */
  targetPolicies?: EditCarrierDto;
}

/** Up to three endpoints; only those with a change are called. */
export function splitOrganisationChanges(changes: CarrierGroupChanges): OrganisationChanges {
  const result: OrganisationChanges = {};
  if (changes.name) {
    result.organisation = { ...result.organisation, name: changes.name };
  }
  if (changes.loginMethod) {
    result.organisation = { ...result.organisation, loginMethod: changes.loginMethod };
  }
  if (changes.registrationTarget) {
    result.registrationTarget = changes.registrationTarget;
  }
  const targetPolicies = toEditCarrierDto({
    kioskRegistrationPolicy: changes.kioskRegistrationPolicy,
    qrRegistrationPolicy: changes.qrRegistrationPolicy
  });
  if (Object.keys(targetPolicies).length > 0) {
    result.targetPolicies = targetPolicies;
  }
  return result;
}
